class InstanceSwiper extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute('handle');
    this.debug = false;
    this._initScheduled = false;
    this._resizeTimer = null;
    this._nativeScrollBound = false;
    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', () => this.scheduleInit());
    }
    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.handleResize(), 150);
    };
    window.addEventListener('resize', this._onResize);
  }

  getSwiperElement() {
    return this.querySelector('.swiper--' + this.handle);
  }

  destroyInstance() {
    if (this.instance?.destroy) {
      this.instance.destroy(true, true);
      this.instance = null;
    }
  }

  isSwiperReady() {
    return typeof Swiper !== 'undefined';
  }

  scopeNavigationOptions(options, swiperElement) {
    if (!options?.navigation || !swiperElement) return options;
    const next = swiperElement.querySelector('.swiper-button-next');
    const prev = swiperElement.querySelector('.swiper-button-prev');
    if (next && prev) {
      options.navigation = { nextEl: next, prevEl: prev };
    }
    return options;
  }

  init() {
    const swiperElement = this.getSwiperElement();
    if (!swiperElement || !this.isSwiperReady()) return false;

    if (this.isMobileGallery?.()) {
      swiperElement.classList.remove('use-native-scroll');
    }

    try {
      this.destroyInstance();
      this.options = this.setOptions();
      this.scopeNavigationOptions(this.options, swiperElement);
      this.instance = new Swiper(swiperElement, this.options);

      if (this.isMobileGallery?.()) {
        swiperElement.classList.remove('use-native-scroll');
      }

      this.setAttribute('data-initialized', 'true');
      this.setInteractions();
      return true;
    } catch (error) {
      console.error('Product gallery Swiper init failed', error);
      if (this.isMobileGallery?.()) {
        this.enableNativeScroll(swiperElement);
      }
      return false;
    }
  }

  scheduleInit() {
    if (this._initScheduled) return;
    this._initScheduled = true;
    requestAnimationFrame(() => {
      this._initScheduled = false;
      this.ensureInit();
    });
  }

  ensureInit(attempt = 0) {
    if (this.needsThumbsInstance() && !this.getThumbsInstance()) {
      if (attempt < 60) {
        requestAnimationFrame(() => this.ensureInit(attempt + 1));
      } else {
        this.init() || this.fallbackToNativeScroll();
      }
      return;
    }

    if (!this.isSwiperReady()) {
      if (attempt < 120) {
        setTimeout(() => this.ensureInit(attempt + 1), 50);
      } else {
        this.fallbackToNativeScroll();
      }
      return;
    }

    if (!this.init() && attempt < 120) {
      setTimeout(() => this.ensureInit(attempt + 1), 50);
    } else if (!this.instance) {
      this.fallbackToNativeScroll();
    }
  }

  needsThumbsInstance() {
    return false;
  }

  isMobileGallery() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  fallbackToNativeScroll() {
    const swiperElement = this.getSwiperElement();
    if (swiperElement && this.isMobileGallery()) {
      this.enableNativeScroll(swiperElement);
    }
  }

  enableNativeScroll(swiperElement) {
    if (!swiperElement) return;
    swiperElement.classList.add('use-native-scroll');
    document.documentElement.classList.add('jsload');

    if (this._nativeScrollBound) return;
    this._nativeScrollBound = true;

    const wrapper = swiperElement.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const visibleSlides = () =>
      Array.from(wrapper.querySelectorAll('.swiper-slide')).filter(
        (slide) =>
          !slide.classList.contains('swiper-slide--variant-hidden') &&
          slide.style.display !== 'none' &&
          getComputedStyle(slide).display !== 'none'
      );

    const scrollAmount = () => {
      const slides = visibleSlides();
      if (!slides.length) return wrapper.clientWidth;
      const slide = slides[0];
      return slide.offsetWidth + parseFloat(getComputedStyle(slide).marginRight || 0);
    };

    const scrollToSlide = (index) => {
      const slides = visibleSlides();
      const target = slides[index];
      if (!target) return;
      wrapper.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    };

    const nextBtn = swiperElement.querySelector('.swiper-button-next');
    const prevBtn = swiperElement.querySelector('.swiper-button-prev');

    nextBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      wrapper.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });

    prevBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      wrapper.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });

    const thumbsRoot = this.closest('.main-product__media');
    thumbsRoot?.querySelectorAll('.swiper--product-thumbs .swiper-slide').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (this.instance) return;
        const mediaId = thumb.getAttribute('data-media-id');
        const target = visibleSlides().find(
          (slide) => slide.getAttribute('data-media-id') === mediaId
        );
        if (target) wrapper.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      });
    });
  }

  handleResize() {
    if (!this.instance) return;
    this.instance.update();
  }

  connectedCallback() {
    this.scheduleInit();
  }

  disconnectedCallback() {
    clearTimeout(this._resizeTimer);
    window.removeEventListener('resize', this._onResize);
    this.destroyInstance();
    this.removeAttribute('data-initialized');
    this._nativeScrollBound = false;
  }

  setOptions() {
    this._options = this.getOptionsAsJsonScripts();
    return this._options;
  }

  getOptionsAsJsonScripts() {
    const jsonScriptAutomated = this.querySelector(
      `script#swiper--${this.handle}--automated-options`
    );
    const jsonScriptOverwrite = this.querySelector(
      `script#swiper--${this.handle}--overwrite-options`
    );
    let options = {};
    if (jsonScriptAutomated?.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptAutomated.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptAutomated.textContent);
      }
    }
    if (jsonScriptOverwrite?.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptOverwrite.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptOverwrite.textContent);
      }
    }
    return options;
  }

  getThumbsElement() {
    const thumbsHandle = this.getAttribute('thumbs');
    if (!thumbsHandle) return null;
    return (
      this.closest('.main-product__media')?.querySelector(
        `swiper-product-thumbs[handle="${thumbsHandle}"]`
      ) || document.querySelector(`swiper-product-thumbs[handle="${thumbsHandle}"]`)
    );
  }

  getThumbsInstance() {
    return this.getThumbsElement()?.instance;
  }

  setInteractions() {}
}

class InstanceSwiperProductThumbs extends InstanceSwiper {
  setOptions() {
    super.setOptions();
    if (this.isMobileGallery()) {
      this._options.mousewheel = false;
    }
    this.setCenteredSlides();
    return this._options;
  }

  setCenteredSlides() {
    if (this.handle !== 'product-thumbs') return;

    const afterInit = (swiper) => {
      if (swiper.activeIndex !== 0) swiper.slideTo(0);
      if (!window.matchMedia('(min-width: 990px)').matches) return;

      const swiperWrapper = swiper.wrapperEl;
      const observer = new MutationObserver(() => {
        if (
          swiperWrapper.style?.transform &&
          swiperWrapper.style.transform !== 'translate3d(0px, 0px, 0px)'
        ) {
          swiperWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
        }
      });
      observer.observe(swiperWrapper, { attributes: true, attributeFilter: ['style'] });
    };

    this._options = {
      ...this._options,
      on: { ...(this._options.on || {}), afterInit }
    };
  }

  init() {
    const ok = super.init();
    this.closest('.main-product__media')
      ?.querySelector('swiper-product-gallery')
      ?.scheduleInit?.();
    return ok;
  }
}

if (!customElements.get('swiper-product-thumbs')) {
  customElements.define('swiper-product-thumbs', InstanceSwiperProductThumbs);
}

class InstanceSwiperProductGallery extends InstanceSwiper {
  constructor() {
    super();
    this.isThumbsActive = this.hasAttribute('thumbs');
    this.isZoomActive = this.hasAttribute('zoom');
    this._photoSwipeLightbox = null;
    this._nativeFallbackTimer = null;
  }

  connectedCallback() {
    const swiperElement = this.getSwiperElement();
    if (this.isMobileGallery() && swiperElement) {
      swiperElement.classList.add('use-native-scroll');
      this.enableNativeScroll(swiperElement);
      this._nativeFallbackTimer = setTimeout(() => {
        if (!this.instance) {
          this.destroyInstance();
          this.enableNativeScroll(swiperElement);
        }
      }, 2500);
    }
    this.scheduleInit();
  }

  disconnectedCallback() {
    clearTimeout(this._nativeFallbackTimer);
    super.disconnectedCallback();
  }

  needsThumbsInstance() {
    return this.isThumbsActive && !this.isMobileGallery();
  }

  setOptions() {
    super.setOptions();
    this.setThumbOptions();

    this._options = {
      ...this._options,
      passiveListeners: false,
      touchStartPreventDefault: false,
      preventClicks: true,
      preventClicksPropagation: true,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      watchSlidesProgress: true,
      watchOverflow: true,
      resistanceRatio: 0.85,
      threshold: 5,
      touchAngle: 45
    };

    if (this.isMobileGallery()) {
      this._options.slidesPerView = this._options.slidesPerView || 1.125;
      this._options.spaceBetween = this._options.spaceBetween ?? 8;
      this._options.touchEventsTarget = 'wrapper';
      this._options.simulateTouch = true;
      this._options.allowTouchMove = true;
    }

    return this._options;
  }

  setThumbOptions() {
    if (!this.isThumbsActive || this.isMobileGallery()) return;
    const thumbsInstance = this.getThumbsInstance();
    if (!thumbsInstance) return;
    this._options.thumbs = { swiper: thumbsInstance };
  }

  refresh() {
    if (!this.instance) {
      this.fallbackToNativeScroll();
      this.scheduleInit();
      return;
    }
    this.instance.update();
    this.instance.slideTo(0, 0);
    if (this.instance.thumbs?.swiper) {
      this.instance.thumbs.swiper.update();
      this.instance.thumbs.swiper.slideTo(0, 0);
    }
  }

  setInteractions() {
    if (this.isThumbsActive && !this.isMobileGallery()) {
      this.setThumbsInteraction();
    } else if (this.isThumbsActive && this.isMobileGallery()) {
      this.bindMobileThumbs();
    }
    if (this.isZoomActive) this.photoSwipeLightboxInit();

    this.instance?.on('slideChange', (swiper) => {
      if (window.innerWidth < 750 && swiper.pagination) {
        swiper.pagination.update();
      }
    });
  }

  bindMobileThumbs() {
    const wrapper = this.getSwiperElement()?.querySelector('.swiper-wrapper');
    const thumbs = this.closest('.main-product__media')?.querySelectorAll(
      '.swiper--product-thumbs .swiper-slide'
    );
    if (!wrapper || !thumbs?.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.getAttribute('data-media-id');
        const slides = Array.from(this.instance?.slides || wrapper.querySelectorAll('.swiper-slide')).filter(
          (s) => !s.classList.contains('swiper-slide--variant-hidden')
        );
        const index = slides.findIndex((s) => s.getAttribute('data-media-id') === mediaId);
        if (index < 0) return;
        if (this.instance) {
          this.instance.slideTo(index);
        } else {
          const target = slides[index];
          wrapper.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        }
      });
    });
  }

  setThumbsInteraction() {
    const thumbsSwiper = this.instance?.thumbs?.swiper;
    if (!thumbsSwiper) return;

    this.instance.on('slideChangeTransitionStart', (swiper) => {
      if (swiper.activeIndex !== thumbsSwiper.activeIndex) {
        thumbsSwiper.slideTo(swiper.activeIndex);
      }
    });
  }

  photoSwipeLightboxInit() {
    if (this._photoSwipeLightbox || typeof PhotoSwipeLightbox === 'undefined') return;

    const photoswipeWrapper = this.querySelector('.photoswipe-wrapper');
    if (!photoswipeWrapper) return;

    const closeIcon = document.querySelector('[data-close-icon]');
    const prevIcon = document.querySelector('[data-prev-icon]');
    const nextIcon = document.querySelector('[data-next-icon]');

    const lightbox = new PhotoSwipeLightbox({
      gallery: photoswipeWrapper,
      children: 'a.product__gallery-toggle',
      mainClass: 'pswp--product-media-gallery',
      loop: false,
      showHideAnimationType: 'zoom',
      initialZoomLevel: (z) => (window.innerWidth < 750 ? z.vFill : z.fit),
      secondaryZoomLevel: (z) => (window.innerWidth < 750 ? z.fit : 1),
      pswpModule: PhotoSwipe
    });

    lightbox.addFilter('uiElement', (element, data) => {
      if (data.name === 'close' && closeIcon) element.innerHTML = closeIcon.innerHTML;
      if (data.name === 'arrowPrev' && prevIcon) element.innerHTML = prevIcon.innerHTML;
      if (data.name === 'arrowNext' && nextIcon) element.innerHTML = nextIcon.innerHTML;
      return element;
    });

    lightbox.addFilter('itemData', (itemData) => {
      if (itemData.type === 'html' && itemData.element) {
        return { html: itemData.element.dataset.pswpHtml };
      }
      return itemData;
    });

    lightbox.init();
    this._photoSwipeLightbox = lightbox;
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(
      (media) => Number(media.dataset.mediaId) === id
    );
    if (!mediaFound) return;

    const slide = mediaFound.closest('.swiper-slide');
    if (!slide) return;

    if (!this.instance) {
      const wrapper = this.getSwiperElement()?.querySelector('.swiper-wrapper');
      if (wrapper) wrapper.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      return;
    }

    const slideIndex = Array.from(this.instance.slides).indexOf(slide);
    if (slideIndex >= 0) {
      this.instance.update();
      this.instance.slideTo(slideIndex);
    }
  }
}

if (!customElements.get('swiper-product-gallery')) {
  customElements.define('swiper-product-gallery', InstanceSwiperProductGallery);
}

function bootProductMediaGalleries() {
  document.querySelectorAll('swiper-product-thumbs').forEach((el) => el.scheduleInit?.());
  document.querySelectorAll('swiper-product-gallery').forEach((el) => {
    if (window.matchMedia('(max-width: 749px)').matches) {
      el.enableNativeScroll?.(el.getSwiperElement?.());
    }
    el.scheduleInit?.();
  });
}

['DOMContentLoaded', 'w3-DOMContentLoaded', 'w3-scripts-loaded', 'load', 'pageshow'].forEach(
  (name) => window.addEventListener(name, bootProductMediaGalleries)
);

class ProductMediaInfo extends HTMLElement {
  constructor() {
    super();
    this.init();
    window.addEventListener('resize', this.init.bind(this));
    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', this.init.bind(this));
    }
  }

  init() {
    const slider = document.querySelector('.main-product__media--slider');
    if (!slider) return;
    let containerOffsetWidth = slider.offsetWidth || 330;
    const gridItem = document.querySelector('.main-product__media--grid-item');
    if (gridItem && window.innerWidth > 750) {
      containerOffsetWidth = gridItem.offsetWidth;
    }
    const text = this.querySelector('p:last-child');
    const hidden = this.querySelector('p[aria-hidden]');
    if (!text) return;

    const maxWidth = (containerOffsetWidth - 48) / 2;
    if (text.offsetWidth > maxWidth) {
      this.classList.remove('animation-stopped');
      this.style.cssText = `--marquee-speed: ${(text.offsetWidth / maxWidth) * 8}s`;
      if (hidden) hidden.style.display = '';
    } else {
      this.classList.add('animation-stopped');
      this.style.cssText = '';
      if (hidden) hidden.style.display = 'none';
    }
  }
}

if (!customElements.get('product-media-info')) {
  customElements.define('product-media-info', ProductMediaInfo);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootProductMediaGalleries);
} else {
  bootProductMediaGalleries();
}
